import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Phone, RefreshCw } from 'lucide-react';
import type { ActiveCallItem } from '../../../models/MasterAdmin/communications';
import CommunicationsAlertDialog from '../../../components/Communications/CommunicationsAlertDialog';
import {
  commCardClass,
  commDesktopTableWrapClass,
  commInputClass,
  commMobileCardClass,
  commPrimaryBtnClass,
  commSecondaryBtnClass,
  commSectionDescClass,
  commSectionTitleClass,
} from '../../../components/Communications/communicationsUi';
import {
  connectCommunicationsCall,
  getActiveCommunicationsCalls,
  getCommunicationsSettings,
} from '../../../services/MasterAdmin/communicationsService';

function formatDuration(seconds?: number): string {
  if (seconds == null) return '—';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function normalizePhoneForCall(value: string | null): string | null {
  if (!value) return null;
  const digits = value.replace(/\D/g, '');
  if (digits.length < 10) return null;
  return value.trim().startsWith('+') ? value.trim() : `+91${digits.slice(-10)}`;
}

type CallAlert = {
  tone: 'success' | 'error' | 'warning';
  title: string;
  message: string;
};

const CommunicationsClickToCallPanel: React.FC = () => {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [record, setRecord] = useState(true);
  const [placing, setPlacing] = useState(false);
  const [loadingActive, setLoadingActive] = useState(false);
  const [loadingSetup, setLoadingSetup] = useState(true);
  const [exoPhoneConfigured, setExoPhoneConfigured] = useState(false);
  const [credentialsConfigured, setCredentialsConfigured] = useState(false);
  const [callAlert, setCallAlert] = useState<CallAlert | null>(null);
  const [lastCallSid, setLastCallSid] = useState<string | null>(null);
  const [activeCalls, setActiveCalls] = useState<ActiveCallItem[]>([]);

  const showAlert = (alert: CallAlert) => setCallAlert(alert);

  const loadSetup = useCallback(async () => {
    try {
      setLoadingSetup(true);
      const settings = await getCommunicationsSettings();
      setExoPhoneConfigured(Boolean(settings.exoPhone));
      setCredentialsConfigured(
        Boolean(settings.apiKeyConfigured && settings.apiTokenConfigured && settings.accountSid)
      );
      if (settings.executivePhoneNumber) {
        const normalized = normalizePhoneForCall(settings.executivePhoneNumber);
        if (normalized) {
          setFrom((current) => (current.trim() ? current : normalized));
        }
      }
    } catch {
      setExoPhoneConfigured(false);
      setCredentialsConfigured(false);
    } finally {
      setLoadingSetup(false);
    }
  }, []);

  const loadActive = useCallback(async () => {
    try {
      setLoadingActive(true);
      const data = await getActiveCommunicationsCalls();
      setActiveCalls(data.active_calls || []);
    } catch {
      setActiveCalls([]);
    } finally {
      setLoadingActive(false);
    }
  }, []);

  useEffect(() => {
    void loadSetup();
    void loadActive();
    const timer = window.setInterval(() => void loadActive(), 15000);
    return () => window.clearInterval(timer);
  }, [loadActive, loadSetup]);

  const handlePlaceCall = async () => {
    if (!credentialsConfigured) {
      showAlert({
        tone: 'warning',
        title: 'Setup required',
        message: 'Configure Exotel API credentials in Settings before placing calls.',
      });
      return;
    }
    if (!exoPhoneConfigured) {
      showAlert({
        tone: 'warning',
        title: 'Setup required',
        message: 'Configure your ExoPhone (Caller ID) in Settings before placing calls.',
      });
      return;
    }

    try {
      setPlacing(true);
      const result = await connectCommunicationsCall({ from: from.trim(), to: to.trim(), record });
      setLastCallSid(result.callSid);
      showAlert({
        tone: 'success',
        title: 'Call initiated',
        message: `Your phone will ring first, then the customer will be connected.${
          result.callSid ? `\n\nCall SID: ${result.callSid}` : ''
        }\nStatus: ${result.status}`,
      });
      void loadActive();
    } catch (err: unknown) {
      showAlert({
        tone: 'error',
        title: 'Call failed',
        message: err instanceof Error ? err.message : 'Failed to place call. Please try again.',
      });
    } finally {
      setPlacing(false);
    }
  };

  return (
    <>
      <CommunicationsAlertDialog
        open={Boolean(callAlert)}
        tone={callAlert?.tone || 'error'}
        title={callAlert?.title || ''}
        message={callAlert?.message || ''}
        onClose={() => setCallAlert(null)}
      />

      <div className="space-y-6">
        <section className={commCardClass}>
          <h2 className={commSectionTitleClass}>Place a call</h2>
          <p className={commSectionDescClass}>
            Connects the From number first, then dials To. Uses your configured ExoPhone as Caller ID.
            {!loadingSetup && (!credentialsConfigured || !exoPhoneConfigured) ? (
              <span className="mt-2 block text-amber-700 dark:text-amber-300">
                {!credentialsConfigured
                  ? 'Exotel API credentials are missing — save them in Settings first.'
                  : 'ExoPhone (Caller ID) is missing — add it in Settings first.'}
              </span>
            ) : null}
          </p>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                From (called first)
              </label>
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                placeholder="10-digit mobile"
                className={commInputClass}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">To</label>
              <input
                type="tel"
                inputMode="tel"
                autoComplete="tel"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="10-digit mobile"
                className={commInputClass}
              />
            </div>
          </div>
          <label className="mt-4 flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
            <input type="checkbox" checked={record} onChange={(e) => setRecord(e.target.checked)} className="rounded" />
            Record this call
          </label>
          <div className="mt-4">
            <button
              type="button"
              onClick={() => void handlePlaceCall()}
              disabled={
                placing ||
                loadingSetup ||
                !from.trim() ||
                !to.trim() ||
                !credentialsConfigured ||
                !exoPhoneConfigured
              }
              className={commPrimaryBtnClass}
            >
              {placing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Phone className="h-4 w-4" />}
              Place call
            </button>
          </div>
          {lastCallSid ? (
            <p className="mt-3 break-all font-mono text-xs text-gray-500 dark:text-gray-400">
              Last Call SID: {lastCallSid}
            </p>
          ) : null}
        </section>

        <section className={commCardClass}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className={commSectionTitleClass}>Active calls</h2>
              <p className={commSectionDescClass}>Live calls from Exotel (refreshes every 15s).</p>
            </div>
            <button
              type="button"
              onClick={() => void loadActive()}
              disabled={loadingActive}
              className={commSecondaryBtnClass}
            >
              {loadingActive ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Refresh
            </button>
          </div>

          {activeCalls.length === 0 ? (
            <p className="mt-4 py-6 text-center text-sm text-gray-500">
              {loadingActive ? 'Loading…' : 'No active calls right now.'}
            </p>
          ) : (
            <>
              <div className="mt-4 space-y-3 md:hidden">
                {activeCalls.map((call) => (
                  <div key={call.call_sid} className={commMobileCardClass}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-xs font-medium uppercase text-gray-500">From</p>
                        <p className="break-all text-sm font-medium text-gray-900 dark:text-white">
                          {call.from || '—'}
                        </p>
                      </div>
                      <span className="shrink-0 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-300">
                        {call.status || '—'}
                      </span>
                    </div>
                    <div className="mt-3">
                      <p className="text-xs font-medium uppercase text-gray-500">To</p>
                      <p className="break-all text-sm text-gray-700 dark:text-gray-300">{call.to || '—'}</p>
                    </div>
                    <p className="mt-2 text-xs text-gray-500">Duration: {formatDuration(call.duration)}</p>
                  </div>
                ))}
              </div>

              <div className={`mt-4 ${commDesktopTableWrapClass}`}>
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs uppercase text-gray-500">
                      <th className="pb-2 pr-4">From</th>
                      <th className="pb-2 pr-4">To</th>
                      <th className="pb-2 pr-4">Status</th>
                      <th className="pb-2">Duration</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activeCalls.map((call) => (
                      <tr key={call.call_sid} className="border-t border-gray-200 dark:border-gray-700">
                        <td className="py-2 pr-4">{call.from || '—'}</td>
                        <td className="py-2 pr-4">{call.to || '—'}</td>
                        <td className="py-2 pr-4">{call.status || '—'}</td>
                        <td className="py-2">{formatDuration(call.duration)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      </div>
    </>
  );
};

export default CommunicationsClickToCallPanel;
